import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { OrderItemsCreateRequestModel } from '@models/api-models/request-models/orderItemsCreateRequestModel';
import { OrderItemModel } from '@models/order-item';
import { OrderItemMiscModel } from '@models/order-item-misc-model';
import { OrderItemState } from '@models/order-item-state';
import { PriceModel, PriceModelStatus, PriceResponse } from '@models/price-model';
import { getTempleInclinationEnumTranslation, TempleInclinationEnum } from '@models/variant-temple-inclination';
import { AppState } from '@shared/app-state';
import { plainToClass } from 'class-transformer';
import { HttpService } from './http.service';
import { LanguageService } from './language.service';
import { SummaryService } from './v2/summary.service';

@Injectable({
	providedIn: 'root'
})
export class OrderItemService {

	constructor(
		private httpService: HttpService,
		private appState: AppState,
		private languageService: LanguageService,
		private summaryService: SummaryService
	) {
	}

	async moveOrderMiscItem(Uuid: string, orderId: number) {
		await this.httpService.postWithOfflineHandling<number>(`${environment.dataUrl}/orders/OrderItemMisc/${Uuid}/MoveTo/${orderId}`, {});
	}

	async moveOrderItem(Uuid: string, orderId: number) {
		await this.httpService.postWithOfflineHandling<number>(`${environment.dataUrl}/orders/OrderItem/${Uuid}/MoveTo/${orderId}`, {});
	}

	private readonly editOrderItemQueues = new Map<string, Promise<OrderItemModel>>();

	public editOrderItem(orderItem: OrderItemModel): Promise<OrderItemModel> {
		const key = orderItem.Uuid;
		const previous = this.editOrderItemQueues.get(key) ?? Promise.resolve(orderItem);
		const current = previous.then(
			() => this.postOrderItemEdit(orderItem),
			() => this.postOrderItemEdit(orderItem)
		);

		this.editOrderItemQueues.set(key, current);
		void current.finally(() => {
			if (this.editOrderItemQueues.get(key) === current) {
				this.editOrderItemQueues.delete(key);
			}
		});

		return current;
	}

	private async postOrderItemEdit(orderItem: OrderItemModel): Promise<OrderItemModel> {
		const state = await this.httpService.postWithOfflineHandling<OrderItemState>(`${environment.dataUrl}/orders/orderItem/edit`, orderItem.getState());
		return new OrderItemModel(plainToClass(OrderItemState, state));
	}

	public async editOrderMiscItem(orderItem: OrderItemMiscModel): Promise<void> {
		await this.httpService.postWithOfflineHandling<void>(`${environment.dataUrl}/orders/OrderItemMisc/edit`, orderItem.getState());
	}

	public async getPriceFromConfiguration(orderItem: OrderItemModel): Promise<PriceResponse> {
		let priceModel = new PriceResponse();

		try {
			if (this.appState.priceModuleEnabled.getValue()) {
				priceModel = await this.httpService.postWithOfflineHandling<PriceResponse>(`${environment.dataUrl}/priceFromConfiguration`, orderItem.getState());

				if (!priceModel?.data) {
					priceModel.status = new PriceModelStatus(priceModel.status?.statusCode, priceModel.status?.message);
				}
			}
			else {
				priceModel.data = new PriceModel()
				priceModel.data.responseItems = []
			}

			return priceModel;
		}
		catch (err: any) {
			console.error('getPrice error', err);

			if (err.error && err.error.substring && err.error.indexOf) {
				priceModel.status = new PriceModelStatus(err.status, err.error.substring(0, err.error.indexOf('\r\n')));
			}

			return priceModel;
		}
	}

	public async postItem(orderItemCreateModel: OrderItemState): Promise<OrderItemState> {
		const result = await this.httpService.postWithOfflineHandling<OrderItemState>(`${environment.dataUrl}/orders/OrderItem/Create`, orderItemCreateModel);
		return result;
	}

	public async postItems(orderItemStates: OrderItemState[]): Promise<OrderItemState[]> {
		const result = await this.httpService.postWithOfflineHandling<OrderItemState[]>(`${environment.dataUrl}/orders/OrderItems/Create`, orderItemStates);
		return result;
	}

	public async postItemsByCollectionId(requestModel: OrderItemsCreateRequestModel): Promise<boolean> {
		const result = await this.httpService.postWithOfflineHandling<boolean>(`${environment.dataUrl}/orders/OrderItems/Create`, requestModel);
		return result;
	}


	public async deleteOrderItem(uuid: string): Promise<void> {
		const result = await this.httpService.deleteWithOfflineHandling<void>(`${environment.dataUrl}/orders/orderItem/${uuid}`);
		return result;
	}

	public GetVariantString(orderItem: OrderItemModel): string {
		if (!orderItem) {
			return '';
		}

		const variantItems: string[] = [];

		if (orderItem.ModelCode) {
			variantItems.push(orderItem.ModelCode);
		}

		if (orderItem.ModelSizeCode && orderItem.ModelSizeCode.split('#').length > 1) {
			variantItems.push(orderItem.ModelSizeCode.split('#')[0]);
		}

		if (orderItem.OrderLines && orderItem.OrderLines.length > 0 && orderItem.OrderLines[0].ComponentVariantCode) {
			variantItems.push(orderItem.OrderLines[0].ComponentVariantCode);
		}

		if (orderItem.OrderLines && orderItem.OrderLines.length > 0 && orderItem.OrderLines[0].ComponentSizeCode) {
			variantItems.push(orderItem.OrderLines[0].ComponentSizeCode);
		}

		const templeInclination = getTempleInclinationEnumTranslation(orderItem.TempleInclination ?? TempleInclinationEnum.standard, this.languageService);
		if (templeInclination && templeInclination !== '') {
			variantItems.push(templeInclination);
		}

		if (orderItem.ModelConfigurationCode) {
			variantItems.push(orderItem.ModelConfigurationCode);
		}

		if (orderItem.ModelConfigurationDescription) {
			variantItems.push(orderItem.ModelConfigurationDescription);
		}

		return variantItems.join(', ');
	}

	public GetMiscItemString(orderItemMiscModel: OrderItemMiscModel): string {
		if (!orderItemMiscModel) {
			return '';
		}

		const items: string[] = [];

		if (orderItemMiscModel.Description) {
			items.push(orderItemMiscModel.Description);
		}

		if (orderItemMiscModel.Ecommerce) {
			items.push(orderItemMiscModel.Ecommerce);
		}

		return items.join(', ');
	}

}
